import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 20;
const USABLE_W_MM = A4_WIDTH_MM - 2 * MARGIN_MM;
const USABLE_H_MM = A4_HEIGHT_MM - 2 * MARGIN_MM;
const BG_COLOR = "#D3E9FF";
const CONTENT_PADDING_PX = 40;
const SECTION_GAP_PX = 40;

interface PdfChunk {
  canvas: HTMLCanvasElement;
  heightMm: number;
  type: "header" | "question";
}

@Injectable({ providedIn: "root" })
export class QuestionnairePdfService {
  private readonly translationService = inject(TranslocoService);

  async download(container: HTMLElement): Promise<void> {
    const sectionEls = Array.from(
      container.querySelectorAll<HTMLElement>("questionnaire-section")
    );
    if (sectionEls.length === 0) return;

    const cleanup = this.prepareForCapture(container);
    await this.waitForRender();

    try {
      const sectionWidth =
        sectionEls[0].querySelector<HTMLElement>("details")?.offsetWidth ?? 800;
      const pxToMm = USABLE_W_MM / sectionWidth;

      const chunks: PdfChunk[] = [];
      for (const sectionEl of sectionEls) {
        chunks.push(...(await this.renderSection(sectionEl, sectionWidth)));
      }

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });
      this.layoutOnPdf(pdf, chunks, SECTION_GAP_PX * pxToMm);
      const documentName = this.translationService.translate(
        "portalQuestionnaire.mobilityQuestionnaireDownloadName"
      );
      pdf.save(`${documentName}-${new Date().toISOString().split("T")[0]}.pdf`);
    } finally {
      cleanup();
    }
  }

  /** Opens all details, hides non-print elements, returns a restore function. */
  private prepareForCapture(container: HTMLElement): () => void {
    const detailsEls = Array.from(
      container.querySelectorAll<HTMLDetailsElement>("details")
    );
    const previousStates = detailsEls.map((d) => d.open);
    detailsEls.forEach((d) => (d.open = true));

    const submitBtn = container.querySelector<HTMLElement>("app-button");
    const prevDisplay = submitBtn?.style.display ?? "";
    if (submitBtn) submitBtn.style.display = "none";

    return () => {
      detailsEls.forEach((d, i) => (d.open = previousStates[i]));
      if (submitBtn) submitBtn.style.display = prevDisplay;
    };
  }

  /** Renders a section as individual chunks: one header + one per question. */
  private async renderSection(
    sectionEl: HTMLElement,
    widthPx: number
  ): Promise<PdfChunk[]> {
    const details = sectionEl.querySelector<HTMLDetailsElement>("details")!;
    const summary = details.querySelector<HTMLElement>("summary")!;
    const contentDiv = details.querySelector<HTMLElement>(":scope > div");
    const questions = contentDiv
      ? Array.from(contentDiv.querySelectorAll<HTMLElement>(":scope > div"))
      : [];

    const chunks: PdfChunk[] = [];
    chunks.push(
      await this.captureHeader(summary, widthPx, questions.length === 0)
    );

    for (let i = 0; i < questions.length; i++) {
      chunks.push(
        await this.captureQuestion(
          questions[i],
          widthPx,
          i === 0,
          i === questions.length - 1
        )
      );
    }

    return chunks;
  }

  /** Captures the section title bar (summary) as an image. */
  private async captureHeader(
    summary: HTMLElement,
    widthPx: number,
    isOnlyBlock: boolean
  ): Promise<PdfChunk> {
    const wrapper = this.createOffscreenWrapper(widthPx);
    const clone = summary.cloneNode(true) as HTMLElement;
    clone.querySelectorAll(".no-print").forEach((el) => el.remove());
    clone.style.borderRadius = isOnlyBlock ? "1.5rem" : "1.5rem 1.5rem 0 0";
    wrapper.appendChild(clone);

    document.body.appendChild(wrapper);
    const canvas = await this.capture(wrapper);
    wrapper.remove();

    return { canvas, heightMm: this.toHeightMm(canvas), type: "header" };
  }

  /** Captures a single question/answer block as an image. */
  private async captureQuestion(
    questionDiv: HTMLElement,
    widthPx: number,
    isFirst: boolean,
    isLast: boolean
  ): Promise<PdfChunk> {
    const topPad = isFirst ? CONTENT_PADDING_PX : CONTENT_PADDING_PX / 2;
    const bottomPad = isLast ? CONTENT_PADDING_PX : CONTENT_PADDING_PX / 2;

    const wrapper = this.createOffscreenWrapper(widthPx);
    wrapper.style.backgroundColor = "white";
    wrapper.style.padding = `${topPad}px ${CONTENT_PADDING_PX}px ${bottomPad}px ${CONTENT_PADDING_PX}px`;
    if (isLast) wrapper.style.borderRadius = "0 0 1.5rem 1.5rem";

    const clone = questionDiv.cloneNode(true) as HTMLElement;
    this.copyFormValues(questionDiv, clone);
    wrapper.appendChild(clone);

    document.body.appendChild(wrapper);
    const canvas = await this.capture(wrapper);
    wrapper.remove();

    return { canvas, heightMm: this.toHeightMm(canvas), type: "question" };
  }

  /** Places chunks sequentially on PDF pages, never splitting a block. */
  private layoutOnPdf(
    pdf: jsPDF,
    chunks: PdfChunk[],
    sectionGapMm: number
  ): void {
    const pageBottom = MARGIN_MM + USABLE_H_MM;
    let y = MARGIN_MM;

    this.fillPageBackground(pdf);

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      y = this.adjustPositionForChunk(
        pdf,
        chunks,
        i,
        y,
        pageBottom,
        sectionGapMm
      );

      pdf.addImage(
        chunk.canvas.toDataURL("image/jpeg"),
        "JPEG",
        MARGIN_MM,
        y,
        USABLE_W_MM,
        chunk.heightMm
      );
      y += chunk.heightMm;
    }
  }

  private createOffscreenWrapper(widthPx: number): HTMLDivElement {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.left = "-9999px";
    div.style.top = "0";
    div.style.width = `${widthPx}px`;
    div.style.boxSizing = "border-box";
    return div;
  }

  private capture(element: HTMLElement): Promise<HTMLCanvasElement> {
    return html2canvas(element, {
      useCORS: true,
      logging: false,
      backgroundColor: BG_COLOR,
    });
  }

  /** Copies textarea values and radio checked states that cloneNode doesn't preserve. */
  private copyFormValues(source: HTMLElement, clone: HTMLElement): void {
    const srcTextareas = source.querySelectorAll("textarea");
    const cloneTextareas = clone.querySelectorAll("textarea");
    srcTextareas.forEach((ta, idx) => {
      if (cloneTextareas[idx]) {
        cloneTextareas[idx].value = ta.value;
        cloneTextareas[idx].textContent = ta.value;
      }
    });

    const srcRadios = source.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]'
    );
    const cloneRadios = clone.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]'
    );
    srcRadios.forEach((r, idx) => {
      if (cloneRadios[idx]) {
        cloneRadios[idx].checked = r.checked;
        if (r.checked) cloneRadios[idx].setAttribute("checked", "checked");
        else cloneRadios[idx].removeAttribute("checked");
      }
    });
  }

  private toHeightMm(canvas: HTMLCanvasElement): number {
    return (canvas.height * USABLE_W_MM) / canvas.width;
  }

  private waitForRender(): Promise<void> {
    return new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
  }

  private fillPageBackground(pdf: jsPDF): void {
    pdf.setFillColor(211, 233, 255);
    pdf.rect(0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, "F");
  }

  private adjustPositionForChunk(
    pdf: jsPDF,
    chunks: PdfChunk[],
    index: number,
    currentY: number,
    pageBottom: number,
    sectionGapMm: number
  ): number {
    const chunk = chunks[index];
    let y = currentY;

    if (chunk.type === "header") {
      if (y > MARGIN_MM) {
        y += sectionGapMm;
      }

      const nextChunk =
        index + 1 < chunks.length && chunks[index + 1].type === "question"
          ? chunks[index + 1]
          : null;
      const minH = chunk.heightMm + (nextChunk?.heightMm ?? 0);

      if (y + minH > pageBottom && y > MARGIN_MM) {
        pdf.addPage();
        this.fillPageBackground(pdf);
        y = MARGIN_MM;
      }
    } else if (y + chunk.heightMm > pageBottom && y > MARGIN_MM) {
      pdf.addPage();
      this.fillPageBackground(pdf);
      y = MARGIN_MM;
    }

    return y;
  }
}
